import { BaseView } from "./base-view";
import { Navigation } from "../navigation";
import { AssignmentDemandRole } from "../enums/AssignmentDemandRole";
import { PageContext } from "../page-context";
import { getCurrentCalendarWeekId, getWeekNumber } from "../common";
import { CalendarWeek } from "../models/CalendarWeek";
import { Chart, registerables } from "chart.js";
import { DemandStatus } from "../enums/DemandStatus";
import { AssignmentDemand } from "../models/AssignmentDemand";

export class HomeView extends BaseView {
  static title = "Home";
  static icon = "bi bi-box";

  /**
   * constructor
   * @param {PageContext} context - The context of the page.
   * @param {string} [container=".main"] - The container to render the view in.
   */
  constructor(context: PageContext, container = ".main") {
    super(context, container);
  }

  connectedCallback(): this {
    super.connectedCallback();
    Chart.register(...registerables);

    this.innerHTML = `<h1><i class="${HomeView.icon}"></i> Assignments</h1>

        <p>
        Welcome! Assignments is a tool to help you manage your resources.</p>

        <div class="py-3 bg-body-tertiary">
            <div class="container">
        
              <div class="row row-cols-1 row-cols-sm-2 row-cols-md-3 g-3">

        ${Object.keys(Navigation.nav)
        .map((key) => {
          if (key == "/") return "";
          return `<div class="col">
                  <div class="card shadow-sm bg-light" style="height: 220px; justify-content: center;">
                    <a href="#${key}" class="text-dark text-decoration-none">
                      <div class="card-body text-center text-uppercase ">
                        <h1 class="fs-1"><i class="${Navigation.nav[key].icon}"></i></h1>
                        <p class="card-text fs-5">${Navigation.nav[key].title}</p>
                      </div>
                    </a>
                  </div>
                </div>`;
        })
        .join("")}
          
                    </div>
                </div>
            </div>`;
    return this;
  }

  onRendered() {
    super.onRendered();
    //async load chart
    this.context.store.assignmentChangeUnsubscribeAll();
    this.loadCharts($(this).find(".container .row"));
  }

  async loadCharts(container: JQuery<HTMLElement>) {
    const template = `<div class="col">
        <div class="card shadow-sm bg-light py-4" style="height: 220px; justify-content: center;">
          <div class="card-body text-center">
            <h1 class="fs-6 text-uppercase">...</h1>
            <span class="placeholder col-7"></span>
            <canvas></canvas>
          </div>
        </div>
      </div>`;

    await this.checkVersionAsync();
    const assignments = this.context.store.getAssignmentsWithCalculatedEmployeesWeeklyHours();
    const assignmentsPromise = this.context.store.getEmployeeAssignments(true, assignments);

    let demandChartData = this.getDemandChartData(assignments);
    let resourceUtilizationChartData = this.getResourceUtilizationChartData(assignmentsPromise);
    let totalUtilizationPercentageByWeekChartData = this.getTotalUtilizationPercentageByWeekChartData(assignmentsPromise);

    container.append(template);
    let card = container.find(".card").last();

    // Load charts one by one
    this.loadDemandChart(card, demandChartData);

    container.append(template);
    card = container.find(".card").last();

    this.loadResourceUtilizationChart(card, resourceUtilizationChartData);

    container.append(template);
    card = container.find(".card").last();

    this.loadTotalUtilizationPercentageByWeekChart(card,totalUtilizationPercentageByWeekChartData);
  }

  async getDemandChartData(
    assignmentsPromise: Promise<AssignmentDemand[]>
  ) {
    let assignments =
      await assignmentsPromise;

    assignments = assignments.filter(
      (a) => a.createdDate > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    );

    const assignmentDemandStatuses = [
      DemandStatus.Open,
      DemandStatus.OpenOnHold,
      DemandStatus.Resolved,
      DemandStatus.Lost,
      DemandStatus.Dropped,
    ];
    return assignmentDemandStatuses.map(
      (status) => assignments.filter((a) => a.status == status).length
    );
  }

  async getResourceUtilizationChartData(
    assignmentsPromise: Promise<AssignmentDemand[]>
  ) {
    const assignments = await assignmentsPromise;
    const currentCalendarWeek = getWeekNumber();
    const cwField =
      "cw_" + currentCalendarWeek + "_" + new Date().getFullYear();

    return [
      //self
      assignments
        .filter((a) => a.employee && a.employee.id == this.context.user.userId)
        .map((a) => a.plannedWeeks[cwField])
        .avg() || 0,

      //leadees
      assignments
        .filter(
          (a) =>
            a.employee &&
            a.employee.leads &&
            a.employee.leads.includes(this.context.user.userId)
        )
        .map((a) => a.plannedWeeks[cwField])
        .avg() || 0,

      //all
      assignments.map((a) => a.plannedWeeks[cwField]).avg() || 0,
    ];
  }

  async getTotalUtilizationPercentageByWeekChartData(
    assignmentsPromise: Promise<AssignmentDemand[]>
  ) {
    const employeeAssignments = await assignmentsPromise;
    const currentCalendarWeekId = getCurrentCalendarWeekId();
    let calendarWeeks = this.context.store.getCalendarWeeks();
    const currentCalendarWeekIndex = calendarWeeks.findIndex(
      (cw) => cw.key === currentCalendarWeekId
    );

    // only take last, current, and next 4 weeks
    calendarWeeks = [
      currentCalendarWeekIndex - 1,
      currentCalendarWeekIndex,
      currentCalendarWeekIndex + 1,
      currentCalendarWeekIndex + 2,
      currentCalendarWeekIndex + 3,
      currentCalendarWeekIndex + 4,
    ].map((index) => calendarWeeks[index]);

    return calendarWeeks.map((cw) => {
      const totalEmployeesContractedWeeklyHours = employeeAssignments.reduce(
        (total, employeeAssignment) =>
          total +
          (employeeAssignment.employee
            ? employeeAssignment.employee.weeklyHours
            : 0),
        0
      );
      const totalUtilizationHours = employeeAssignments.reduce(
        (total, employeeAssignment) => {
          const result = (employeeAssignment.assignments || []).reduce(
            (t, assignment) =>
              t +
              (assignment.role ===
                AssignmentDemandRole.SuggestionsAssignmentRole
                ? 0
                : assignment.plannedWeeks[cw.key] || 0),
            0
          );

          return total + result;
        },
        0
      );

      return (
        (totalUtilizationHours / totalEmployeesContractedWeeklyHours) * 100
      );
    });
  }

  async loadResourceUtilizationChart(
    card: JQuery<HTMLElement>,
    data: any
  ) {
    card.find("h1").text("Resources planned, this week");
    const ctx = card.find("canvas")[0];

    const chart = new Chart(ctx, {
      type: "bar",
      data: {
        labels: ["Self", "My leadees", "All"],
        datasets: [
          {
            label: "hours planned",
            backgroundColor: "#00A4B3",
            borderColor: "#00A4B3",
            data: [], // Initialize with empty data
            borderWidth: 1,
          },
        ],
      },
      options: {
        scales: {
          y: {
            beginAtZero: true,
          },
        },
      },
    });

    const chartData = await data;
    chart.data.datasets[0].data = chartData;

    card.find(".placeholder").hide();

    chart.update();
  }

  async loadDemandChart(card: JQuery<HTMLElement>, data: any) {
    card.find("h1").text("Demand Status last 30 days");
    const ctx = card.find("canvas")[0];

    const assignmentDemandStatuses = [
      DemandStatus.Open,
      DemandStatus.OpenOnHold,
      DemandStatus.Resolved,
      DemandStatus.Lost,
      DemandStatus.Dropped,
    ];

    const chart = new Chart(ctx, {
      type: "bar",
      data: {
        labels: assignmentDemandStatuses,
        datasets: [
          {
            label: "# of Demands",
            backgroundColor: "#00A4B3",
            borderColor: "#00A4B3",
            data: [], // Initialize with empty data
            borderWidth: 1,
          },
        ],
      },
      options: {
        scales: {
          y: {
            beginAtZero: true,
          },
        },
      },
    });

    const chartData = await data;
    chart.data.datasets[0].data = chartData;

    card.find(".placeholder").hide();

    chart.update();
  }

  async loadTotalUtilizationPercentageByWeekChart(
    card: JQuery<HTMLElement>,
    data: any
  ) {
    card.find("h1").text("Total Utilization Percentage by Week");
    const ctx = card.find("canvas")[0];

    const currentCalendarWeekId = getCurrentCalendarWeekId();
    let calendarWeeks = this.context.store.getCalendarWeeks();
    const currentCalendarWeekIndex = calendarWeeks.findIndex(
      (cw) => cw.key === currentCalendarWeekId
    );

    // only take last, current, and next 4 weeks
    calendarWeeks = [
      currentCalendarWeekIndex - 1,
      currentCalendarWeekIndex,
      currentCalendarWeekIndex + 1,
      currentCalendarWeekIndex + 2,
      currentCalendarWeekIndex + 3,
      currentCalendarWeekIndex + 4,
    ].map((index) => calendarWeeks[index]);

    const chart = new Chart(ctx, {
      type: "line",
      data: {
        labels: calendarWeeks.map((cw) => createCalendarWeekLabel(cw)),
        datasets: [
          {
            label: "% of Total Utilization",
            backgroundColor: "#00A4B3",
            borderColor: "#00A4B3",
            borderWidth: 1,
            data: [], // Initialize with empty data
          },
        ],
      },
      options: {
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
          },
          x: {
            ticks: {
              showLabelBackdrop: true,
              backdropColor: "#00A4B3",
              color: "white",
            },
          },
        },
      },
    });

    // Await the data and then update the chart
    const chartData = await data;
    chart.data.datasets[0].data = chartData;

    card.find(".placeholder").hide();

    chart.update();

    /**
     * @param {CalendarWeek} cw
     * @returns {string}
     */
    function createCalendarWeekLabel(cw: CalendarWeek) {
      return `W${cw.cw}/${cw.year}`;
    }
  }
}
